import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { AlertTriangle, ArrowLeft, Loader2, RefreshCw } from 'lucide-react'
import {
  ADMIN_CONTENT_MIGRATION_MODES,
  buildAdminContentDashboardUrl,
  getAdminContentMigrationMode,
  isAdminContentMigrationDisabledError,
  replaceWithAdminContentRoute,
  resolveLegacyAdminContentRoute,
  trackLegacyAdminContentRouteUse
} from '@services/adminContentMigrationService'

const RESOLUTION_STATES = Object.freeze({
  LOADING: 'loading',
  REDIRECTING: 'redirecting',
  DISABLED: 'disabled',
  ERROR: 'error'
})
const REDIRECT_WAIT_MS = 1000

function BoundaryStatus({ state, onRetry }) {
  const isLoading =
    state === RESOLUTION_STATES.LOADING ||
    state === RESOLUTION_STATES.REDIRECTING
  const title =
    state === RESOLUTION_STATES.DISABLED
      ? 'Content administration is unavailable'
      : state === RESOLUTION_STATES.ERROR
        ? 'Platform Console status is unavailable'
        : state === RESOLUTION_STATES.REDIRECTING
          ? 'Opening Platform Console'
          : 'Checking administrator access'

  return (
    <main className="flex min-h-screen items-center justify-center bg-[var(--x3-page)] px-6 text-[var(--x3-text)]">
      <div
        className="w-full max-w-lg border-l-2 border-[var(--x3-border-strong)] pl-5"
        role={state === RESOLUTION_STATES.ERROR ? 'alert' : 'status'}
        aria-live="polite"
      >
        <div className="flex items-start gap-3">
          {isLoading ? (
            <Loader2
              className="mt-0.5 h-5 w-5 flex-none animate-spin text-[#0F9D78]"
              aria-hidden="true"
            />
          ) : (
            <AlertTriangle
              className="mt-0.5 h-5 w-5 flex-none text-[#C47A16]"
              aria-hidden="true"
            />
          )}
          <div>
            <h1 className="text-lg font-semibold">{title}</h1>
            {!isLoading && (
              <p className="mt-2 text-sm leading-6 text-[var(--x3-text-muted)]">
                {state === RESOLUTION_STATES.DISABLED
                  ? 'This content workflow is disabled for the current administrator session.'
                  : 'The Platform Console destination was not opened because its access state could not be verified.'}
              </p>
            )}
          </div>
        </div>

        {!isLoading && (
          <div className="mt-5 flex flex-wrap items-center gap-2">
            {state === RESOLUTION_STATES.ERROR && (
              <button
                type="button"
                onClick={onRetry}
                className="inline-flex min-h-10 items-center gap-2 bg-[#153B5B] px-4 text-sm font-medium text-white hover:bg-[#0F2D47]"
              >
                <RefreshCw className="h-4 w-4" aria-hidden="true" />
                Retry
              </button>
            )}
            <Link
              to="/blog"
              className="inline-flex min-h-10 items-center gap-2 px-4 text-sm font-medium text-[var(--x3-text-muted)] hover:bg-[var(--x3-surface-subtle)] hover:text-[var(--x3-text)]"
            >
              <ArrowLeft className="h-4 w-4" aria-hidden="true" />
              Back to blog
            </Link>
          </div>
        )}
      </div>
    </main>
  )
}

export default function AdminContentMigrationBoundary() {
  const location = useLocation()
  const route = useMemo(
    () => resolveLegacyAdminContentRoute(location.pathname),
    [location.pathname]
  )
  const [retryVersion, setRetryVersion] = useState(0)
  const [resolution, setResolution] = useState({
    routeKey: null,
    state: RESOLUTION_STATES.LOADING,
    error: null
  })
  const currentState =
    resolution.routeKey === route?.key
      ? resolution.state
      : RESOLUTION_STATES.LOADING

  useEffect(() => {
    if (!route) return undefined

    const controller = new AbortController()
    setResolution({
      routeKey: route.key,
      state: RESOLUTION_STATES.LOADING,
      error: null
    })

    getAdminContentMigrationMode({
      signal: controller.signal
    })
      .then(({ mode }) => {
        if (controller.signal.aborted) return

        const nextState =
          mode === ADMIN_CONTENT_MIGRATION_MODES.DISABLED
            ? RESOLUTION_STATES.DISABLED
            : RESOLUTION_STATES.REDIRECTING
        setResolution({
          routeKey: route.key,
          state: nextState,
          error: null
        })
      })
      .catch(error => {
        if (controller.signal.aborted) return
        setResolution({
          routeKey: route.key,
          state: isAdminContentMigrationDisabledError(error)
            ? RESOLUTION_STATES.DISABLED
            : RESOLUTION_STATES.ERROR,
          error
        })
      })

    return () => controller.abort()
  }, [retryVersion, route])

  useEffect(() => {
    if (
      !route ||
      currentState !== RESOLUTION_STATES.REDIRECTING
    ) {
      return undefined
    }

    let active = true
    let redirected = false
    let destination

    try {
      destination = buildAdminContentDashboardUrl(
        route,
        undefined,
        { search: location.search }
      )
    } catch (error) {
      setResolution({
        routeKey: route.key,
        state: RESOLUTION_STATES.ERROR,
        error
      })
      return undefined
    }

    const redirect = () => {
      if (!active || redirected) return
      redirected = true
      try {
        replaceWithAdminContentRoute(destination)
      } catch (error) {
        setResolution({
          routeKey: route.key,
          state: RESOLUTION_STATES.ERROR,
          error
        })
      }
    }
    const redirectTimer = window.setTimeout(redirect, REDIRECT_WAIT_MS)

    Promise.resolve()
      .then(() => trackLegacyAdminContentRouteUse(route))
      .catch(() => false)
      .finally(() => {
        window.clearTimeout(redirectTimer)
        redirect()
      })

    return () => {
      active = false
      window.clearTimeout(redirectTimer)
    }
  }, [currentState, location.search, route])

  return (
    <BoundaryStatus
      state={currentState}
      onRetry={() => setRetryVersion(current => current + 1)}
    />
  )
}
